import { useCallback, type SyntheticEvent } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { KnowledgeBaseFormTitle } from '../KnowledgeBaseFormTitle';
import { KnowledgeBaseFormDescription } from '../KnowledgeBaseFormDescription';
import { KnowledgeBaseFormSpaceVisibility } from '../KnowledgeBaseFormSpaceVisibility';
import { KnowledgeBaseFormVisibility } from '@/features/knowledge-bases';
import { KnowledgeBaseFormProps } from './KnowledgeBaseForm.types';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { useTranslation } from 'react-i18next';

const MODAL_TITLE_KEYS = {
  create: 'knowledgeBases.knowledgeBaseForm.newKnowledgeBase',
  edit: 'knowledgeBases.knowledgeBaseForm.editKnowledgeBase',
} as const;

export const KnowledgeBaseForm = ({
  data,
  setData,
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting = false,
  isFormValid,
  children,
  modalType,
}: KnowledgeBaseFormProps) => {
  const { t } = useTranslation('knowledge');

  const handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      void onSubmit(data);
    },
    [data, onSubmit],
  );

  const handleSubmitClick = useCallback(() => {
    void onSubmit(data);
  }, [data, onSubmit]);

  const { knowledgeBaseModalTypeEdit } = useKnowledgeBaseModal();

  const submitLabel = isSubmitting
    ? t('knowledgeBases.knowledgeBaseForm.saving')
    : knowledgeBaseModalTypeEdit
      ? t('knowledgeBases.knowledgeBaseForm.saveChanges')
      : t('knowledgeBases.knowledgeBaseForm.createKnowledgeBase');

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent showCloseButton={!isSubmitting}>
        <DialogHeader>
          <DialogTitle>{t(MODAL_TITLE_KEYS[modalType])}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-8"
            noValidate
            aria-label={
              knowledgeBaseModalTypeEdit
                ? t('knowledgeBases.knowledgeBaseForm.editKnowledgeBaseForm')
                : t('knowledgeBases.knowledgeBaseForm.createKnowledgeBaseForm')
            }
          >
            <KnowledgeBaseFormTitle
              title={data.title}
              setTitle={(next: string) => setData({ ...data, title: next })}
            />
            <KnowledgeBaseFormDescription
              description={data.description}
              setDescription={(next: string) => setData({ ...data, description: next })}
            />
            <KnowledgeBaseFormSpaceVisibility
              spaceVisibility={data.spaceVisibility}
              setSpaceVisibility={(v: KnowledgeBaseFormVisibility) =>
                setData({ ...data, spaceVisibility: v })
              }
            />
            {children}
          </form>
        </DialogBody>

        <DialogFooter>
          {knowledgeBaseModalTypeEdit && onDelete && (
            <Button
              variant="outline"
              onClick={onDelete}
              disabled={isSubmitting}
              className="text-destructive hover:text-destructive sm:mr-auto"
            >
              {t('knowledgeBases.knowledgeBaseForm.delete')}
            </Button>
          )}
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {t('knowledgeBases.knowledgeBaseForm.cancel')}
          </Button>
          <Button onClick={handleSubmitClick} disabled={!isFormValid || isSubmitting}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

KnowledgeBaseForm.displayName = 'KnowledgeBaseForm';
